import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
} from '@mui/material';
import {
  AttachMoney,
  ShoppingCart,
  Inventory,
  TrendingUp,
  Notifications,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';

interface DashboardData {
  totalSales: number;
  totalOrders: number;
  productCount: number;
  growthRate: number;
  growthDisplay: string;
  salesData: Array<{ week: string; sales: number }>;
  notifications: string[];
}

type PeriodFilter = 'today' | 'month' | '3months' | '6months' | 'year' | 'custom';

export default function SellerDashboard() {
  const { t } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user } = useAuth();

  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalSales: 0,
    totalOrders: 0,
    productCount: 0,
    growthRate: 0,
    growthDisplay: '0%',
    salesData: [],
    notifications: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 기간 필터 상태
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('month');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [filteredSalesData, setFilteredSalesData] = useState<Array<{ date: string; sales: number }>>([]);

  // localStorage에서 다크모드 설정 불러오기
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setIsDarkMode(savedDarkMode === 'true');
    }
  }, []);

  // localStorage 변화 감지 (다른 탭에서 변경된 경우)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode) {
        setIsDarkMode(savedDarkMode === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // localStorage 변화를 감지하기 위한 interval (같은 탭 내에서 변경 감지)
    const interval = setInterval(() => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode && savedDarkMode !== isDarkMode.toString()) {
        setIsDarkMode(savedDarkMode === 'true');
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isDarkMode]);

  // 실제 데이터 로드
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }

      // 1. Seller 권한 확인
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profileError || !['seller', 'creator'].includes(profile?.role)) {
        throw new Error('판매자 권한이 없습니다.');
      }

      // 2. seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: sellerProfileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerProfileError || !sellerProfile) {
        throw new Error('판매자 프로필을 찾을 수 없습니다.');
      }

      // 3. 판매자의 상품 조회
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('product_id, product_name')
        .eq('seller_id', sellerProfile.id);

      if (productsError) throw productsError;

      const productIds = products?.map(p => p.product_id) || [];
      const productCount = products?.length || 0;

      if (productIds.length === 0) {
        setDashboardData({
          totalSales: 0,
          totalOrders: 0,
          productCount: 0,
          growthRate: 0,
          growthDisplay: '0%',
          salesData: [],
          notifications: [t('seller.dashboard.registerProductsNotif')]
        });
        return;
      }

      // 4. 해당 상품들의 주문 아이템 조회 (cancel_exchange_request 포함)
      const { data: orderItems, error: orderItemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          products!inner (
            product_id,
            product_name,
            seller_id
          ),
          orders!inner (
            id,
            created_at,
            delivery_status
          ),
          cancel_exchange_request:cancel_exchange_requests(
            id,
            type,
            status
          )
        `)
        .in('product_id', productIds)
        .eq('products.seller_id', sellerProfile.id);

      if (orderItemsError) throw orderItemsError;

      // 취소/환불 제외 필터 함수
      const filterValidOrders = (items: any[]) => {
        return items?.filter(item => {
          // 취소된 주문 제외
          if (item.item_delivery_status === 'cancelled') return false;

          // 승인된 환불/교환 요청 제외
          if (item.cancel_exchange_request &&
              item.cancel_exchange_request.status === 'approved' &&
              (item.cancel_exchange_request.type === 'cancel' ||
               item.cancel_exchange_request.type === 'refund')) {
            return false;
          }

          return true;
        }) || [];
      };

      // 5. 이번 달 매출 계산 (취소/환불 제외)
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const thisMonthItems = filterValidOrders(orderItems || []).filter(item => {
        const orderDate = new Date(item.orders.created_at);
        return orderDate >= currentMonthStart && orderDate <= currentMonthEnd;
      });

      const totalSales = thisMonthItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      const totalOrders = thisMonthItems.length;

      // 6. 주간 매출 데이터 생성 (최근 4주, 취소/환불 제외)
      const weeklyData: { [key: string]: number } = {};

      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (i * 7));
        const weekKey = `Week ${4 - i}`;
        weeklyData[weekKey] = 0;
      }

      const validOrderItems = filterValidOrders(orderItems || []);

      validOrderItems.forEach(item => {
        const orderDate = new Date(item.orders.created_at);
        const weeksAgo = Math.floor((now.getTime() - orderDate.getTime()) / (7 * 24 * 60 * 60 * 1000));

        if (weeksAgo >= 0 && weeksAgo < 4) {
          const weekKey = `Week ${4 - weeksAgo}`;
          if (weeklyData[weekKey] !== undefined) {
            weeklyData[weekKey] += item.total_price || 0;
          }
        }
      });

      const salesData = Object.entries(weeklyData).map(([week, sales]) => ({
        week,
        sales
      }));

      // 7. 성장률 계산 (이번 주 vs 지난 주)
      const thisWeekSales = salesData[3]?.sales || 0;
      const lastWeekSales = salesData[2]?.sales || 0;

      let growthRate = 0;
      let growthDisplay = '0%';

      if (lastWeekSales === 0 && thisWeekSales > 0) {
        // 지난 주 매출이 0이고 이번 주 매출이 있으면
        growthDisplay = '100%↑';
        growthRate = 100;
      } else if (lastWeekSales > 0) {
        // 지난 주 매출이 있으면 정상 계산
        growthRate = ((thisWeekSales - lastWeekSales) / lastWeekSales) * 100;
        const sign = growthRate >= 0 ? '+' : '';
        growthDisplay = `${sign}${growthRate.toFixed(1)}%`;
      } else {
        // 둘 다 0이면
        growthDisplay = '0%';
        growthRate = 0;
      }

      // 8. 알림 생성
      const notifications: string[] = [];
      
      // 최근 주문 알림
      const recentOrders = orderItems?.filter(item => {
        const orderDate = new Date(item.orders.created_at);
        const daysDiff = (now.getTime() - orderDate.getTime()) / (24 * 60 * 60 * 1000);
        return daysDiff <= 1; // 1일 이내
      }) || [];

      if (recentOrders.length > 0) {
        notifications.push(t('seller.dashboard.newOrdersNotif', { count: recentOrders.length }));
      }

      // 재고 부족 옵션 체크
      const { data: lowStockOptions } = await supabase
        .from('product_options')
        .select('quantity, product_id')
        .in('product_id', productIds)
        .lte('quantity', 5);

      if (lowStockOptions && lowStockOptions.length > 0) {
        notifications.push(t('seller.dashboard.lowStockNotif', { count: lowStockOptions.length }));
      }

      // 환불 요청 체크
      const { data: refundRequests } = await supabase
        .from('cancel_exchange_requests')
        .select('id')
        .eq('status', 'pending');

      if (refundRequests && refundRequests.length > 0) {
        notifications.push(t('seller.dashboard.pendingRefundsNotif', { count: refundRequests.length }));
      }

      if (notifications.length === 0) {
        notifications.push(t('seller.dashboard.allGoodNotif'));
      }

      setDashboardData({
        totalSales,
        totalOrders,
        productCount,
        growthRate,
        growthDisplay,
        salesData,
        notifications
      });

    } catch (error) {
      console.error('Dashboard data loading error:', error);
      setError(error instanceof Error ? error.message : t('seller.dashboard.dataLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  const stats = [
    { label: 'Total Sales (This Month)', value: formatCurrency(dashboardData.totalSales), icon: <AttachMoney sx={{ color: '#000' }} />, color: 'black' },
    { label: 'Orders', value: dashboardData.totalOrders.toString(), icon: <ShoppingCart sx={{ color: '#000' }} />, color: 'black' },
    { label: 'Products', value: dashboardData.productCount.toString(), icon: <Inventory sx={{ color: '#000' }} />, color: 'black' },
    { label: 'Growth (Weekly)', value: dashboardData.growthDisplay, icon: <TrendingUp sx={{ color: '#000' }} />, color: 'black' },
  ];

  // 기간별 매출 데이터 계산 (useEffect로 감시)
  useEffect(() => {
    if (user) {
      loadPeriodSalesData();
    }
  }, [periodFilter, customStartDate, customEndDate, user]);

  const loadPeriodSalesData = async () => {
    try {
      if (!user?.id) return;

      // Seller profile 가져오기
      const { data: sellerProfile } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!sellerProfile) return;

      // 판매자의 상품 조회
      const { data: products } = await supabase
        .from('products')
        .select('product_id')
        .eq('seller_id', sellerProfile.id);

      const productIds = products?.map(p => p.product_id) || [];
      if (productIds.length === 0) {
        setFilteredSalesData([]);
        return;
      }

      // 주문 아이템 조회 (취소/환불 정보 포함)
      const { data: orderItems } = await supabase
        .from('order_items')
        .select(`
          *,
          products!inner (
            product_id,
            seller_id
          ),
          orders!inner (
            id,
            created_at
          ),
          cancel_exchange_request:cancel_exchange_requests(
            id,
            type,
            status
          )
        `)
        .in('product_id', productIds)
        .eq('products.seller_id', sellerProfile.id);

      // 취소/환불 제외 필터링
      const validItems = (orderItems || []).filter(item => {
        if (item.item_delivery_status === 'cancelled') return false;
        if (item.cancel_exchange_request &&
            item.cancel_exchange_request.status === 'approved' &&
            (item.cancel_exchange_request.type === 'cancel' ||
             item.cancel_exchange_request.type === 'refund')) {
          return false;
        }
        return true;
      });

      // 기간 필터 적용
      const now = new Date();
      let startDate: Date;
      let endDate: Date = now;

      switch (periodFilter) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        case '3months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        case '6months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
          break;
        case 'custom':
          if (!customStartDate || !customEndDate) {
            setFilteredSalesData([]);
            return;
          }
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      // 기간 내 주문 필터링 및 그룹화
      const periodItems = validItems.filter(item => {
        const orderDate = new Date(item.orders.created_at);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // 날짜별로 그룹화
      const salesByDate: { [key: string]: number } = {};

      periodItems.forEach(item => {
        const orderDate = new Date(item.orders.created_at);
        const dateKey = orderDate.toISOString().split('T')[0]; // YYYY-MM-DD 형식

        if (!salesByDate[dateKey]) {
          salesByDate[dateKey] = 0;
        }
        salesByDate[dateKey] += item.total_price || 0;
      });

      // 배열로 변환 및 정렬
      const salesArray = Object.entries(salesByDate)
        .map(([date, sales]) => ({ date, sales }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setFilteredSalesData(salesArray);

    } catch (error) {
      console.error('Period sales data loading error:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'transparent',
      minHeight: '100vh'
    }}>
      <Typography variant="h4" fontWeight={700} mb={3} color={isDarkMode ? '#fff' : '#000'}>
        Seller Dashboard
      </Typography>
      <Grid container spacing={3} mb={2}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Card sx={{ 
              borderRadius: 3, 
              bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
              boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
              p: 2,
              transition: 'background-color 0.3s ease, border-color 0.3s ease'
            }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ 
                  bgcolor: isDarkMode ? '#404040' : '#f5f5f5', 
                  color: isDarkMode ? '#fff' : '#000', 
                  width: 56, 
                  height: 56 
                }}>
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700} color={isDarkMode ? '#fff' : '#000'}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color={isDarkMode ? '#ccc' : 'text.secondary'}>
                    {stat.label}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{
            borderRadius: 3,
            p: 3,
            bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
            boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
            transition: 'background-color 0.3s ease, border-color 0.3s ease'
          }}>
            <Typography variant="h6" fontWeight={700} mb={2} color={isDarkMode ? '#fff' : '#000'}>
              Sales Trend (Weekly)
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dashboardData.salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#404040' : '#f0f0f0'} />
                <XAxis dataKey="week" stroke={isDarkMode ? '#ccc' : '#666'} />
                <YAxis stroke={isDarkMode ? '#ccc' : '#666'} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#404040' : '#fff',
                    border: isDarkMode ? '1px solid #606060' : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: isDarkMode ? '#fff' : '#000'
                  }}
                />
                <Line type="monotone" dataKey="sales" stroke={isDarkMode ? '#fff' : '#000'} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* 기간별 매출 차트 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{
            borderRadius: 3,
            p: 3,
            bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
            boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
            transition: 'background-color 0.3s ease, border-color 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" fontWeight={700} color={isDarkMode ? '#fff' : '#000'}>
                Sales by Period
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={periodFilter}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setPeriodFilter(newValue);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: isDarkMode ? '#ccc' : '#666',
                      borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                      '&.Mui-selected': {
                        bgcolor: isDarkMode ? '#404040' : '#f5f5f5',
                        color: isDarkMode ? '#fff' : '#000',
                      }
                    }
                  }}
                >
                  <ToggleButton value="today">{t('seller.dashboard.today')}</ToggleButton>
                  <ToggleButton value="month">{t('seller.dashboard.thisMonth')}</ToggleButton>
                  <ToggleButton value="3months">{t('seller.dashboard.threeMonths')}</ToggleButton>
                  <ToggleButton value="6months">{t('seller.dashboard.sixMonths')}</ToggleButton>
                  <ToggleButton value="year">{t('seller.dashboard.thisYear')}</ToggleButton>
                  <ToggleButton value="custom">{t('seller.dashboard.customPeriod')}</ToggleButton>
                </ToggleButtonGroup>

                {periodFilter === 'custom' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      type="date"
                      size="small"
                      label={t('seller.dashboard.startDate')}
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: isDarkMode ? '#fff' : '#000',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#ccc' : '#666',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                        }
                      }}
                    />
                    <TextField
                      type="date"
                      size="small"
                      label={t('seller.dashboard.endDate')}
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: isDarkMode ? '#fff' : '#000',
                        },
                        '& .MuiInputLabel-root': {
                          color: isDarkMode ? '#ccc' : '#666',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isDarkMode ? '#606060' : '#e0e0e0',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {filteredSalesData.length > 0 ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color={isDarkMode ? '#ccc' : 'text.secondary'}>
                    {t('seller.dashboard.totalSalesColon')} {formatCurrency(filteredSalesData.reduce((sum, item) => sum + item.sales, 0))}
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={filteredSalesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#404040' : '#f0f0f0'} />
                    <XAxis
                      dataKey="date"
                      stroke={isDarkMode ? '#ccc' : '#666'}
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis stroke={isDarkMode ? '#ccc' : '#666'} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#404040' : '#fff',
                        border: isDarkMode ? '1px solid #606060' : '1px solid #e0e0e0',
                        borderRadius: '8px',
                        color: isDarkMode ? '#fff' : '#000'
                      }}
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke={isDarkMode ? '#fff' : '#000'}
                      strokeWidth={2}
                      dot={{ fill: isDarkMode ? '#fff' : '#000', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color={isDarkMode ? '#ccc' : 'text.secondary'}>
                  {t('seller.dashboard.noSalesData')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* 기간별 매출 숫자 표시 */}
        <Grid item xs={12} md={6}>
          <Paper sx={{
            borderRadius: 3,
            p: 3,
            bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
            boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
            transition: 'background-color 0.3s ease, border-color 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" fontWeight={700} mb={3} color={isDarkMode ? '#fff' : '#000'}>
              Period Sales
            </Typography>

            <Box sx={{
              textAlign: 'center',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="h2" fontWeight={700} color={isDarkMode ? '#fff' : '#000'} mb={2}>
                {formatCurrency(filteredSalesData.reduce((sum, item) => sum + item.sales, 0))}
              </Typography>
              <Typography variant="body1" color={isDarkMode ? '#ccc' : 'text.secondary'} mb={1}>
                {periodFilter === 'today' && t('seller.dashboard.today')}
                {periodFilter === 'month' && t('seller.dashboard.thisMonth')}
                {periodFilter === '3months' && t('seller.dashboard.last3Months')}
                {periodFilter === '6months' && t('seller.dashboard.last6Months')}
                {periodFilter === 'year' && t('seller.dashboard.thisYear')}
                {periodFilter === 'custom' && t('seller.dashboard.selectedPeriod')}
                {' '}{t('seller.dashboard.salesUnit')}
              </Typography>
              <Typography variant="caption" color={isDarkMode ? '#999' : 'text.disabled'}>
                {t('seller.dashboard.excludeCancelRefund')}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
} 